import { action, computed, observable, makeObservable } from 'mobx'
import { BehaviorSubject, merge } from 'rxjs'
import { map } from 'rxjs/operators'
import { AudioAnalysis } from 'sound/AudioAnalysis'
import { AudioContext, IAudioContext } from 'standardized-audio-context'
import { asAtom, toStream } from 'utils/mobx-util'
import { filterNonNil, shareLatest, whileActive } from 'utils/rx-util'
import { CallPorts } from './ports'

export default class CallMediaSettingsModalViewModel {
  private readonly active$ = new BehaviorSubject(false)

  @observable showing = false

  @observable previewAudioStream: MediaStream | null = null
  @observable previewVideoStream: MediaStream | null = null

  private audioCtx: IAudioContext = new AudioContext()
  private readonly audioAnalysis$ = AudioAnalysis(
    this.audioCtx,
    toStream(() => this.previewAudioStream, true),
    100
  ).pipe(shareLatest())

  private readonly preview$ = merge(
    toStream(() => this.selectedAudioInputDevice),
    toStream(() => this.selectedVideoInputDevice)
  ).pipe(filterNonNil(), whileActive(this.active$))

  private readonly $ = {
    mediaDevices: asAtom(this.adapter.mediaDevices$, []),
    selectedMediaDevices: asAtom(this.adapter.selectedDevices$, []),
    analysedLoudness: asAtom(
      this.audioAnalysis$.pipe(map((item) => item.loudness)),
      0
    ),
  }

  constructor(private adapter: CallPorts) {
    makeObservable(this)
    this.preview$.subscribe((d) => {
      this.getPreviewMediaStream(d)
    })
  }

  activate() {
    this.active$.next(true)
  }

  // Clear out the streams since we don't need them and stop the tracks.
  @action
  deactivate() {
    this.active$.next(false)
    if (this.previewVideoStream) {
      this.previewVideoStream.getTracks().forEach((t) => t.stop())
    }
    if (this.previewAudioStream) {
      this.previewAudioStream.getTracks().forEach((t) => t.stop())
    }
    this.previewAudioStream = null
    this.previewVideoStream = null
  }

  @computed
  get availableDevices() {
    return this.$.mediaDevices()
  }

  @computed
  get audioInputDevices() {
    return this.$.mediaDevices().filter(
      (d): d is MediaDeviceInfo => d?.kind === 'audioinput'
    )
  }

  /*
  @computed
  get audioOutputDevices() {
    return this.$.mediaDevices().filter(
      (d): d is MediaDeviceInfo => d?.kind === 'audiooutput'
    )
  }
  */

  @computed
  get videoInputDevices() {
    return this.$.mediaDevices().filter(
      (d): d is MediaDeviceInfo => d?.kind === 'videoinput'
    )
  }

  @computed
  get selectedAudioInputDevice() {
    return (
      this.$.selectedMediaDevices().find((d) => d?.kind === 'audioinput') ??
      null
    )
  }

  @computed
  get audioLoudness() {
    return this.$.analysedLoudness()
  }

  /*
  @computed
  get selectedAudioOutputDevice() {
    return (
      this.$.selectedMediaDevices().find((d) => d?.kind === 'audiooutput') ??
      null
    )
  }
  */

  @computed
  get selectedVideoInputDevice() {
    return (
      this.$.selectedMediaDevices().find((d) => d?.kind === 'videoinput') ??
      null
    )
  }

  @action.bound
  async selectMediaDevice(device?: MediaDeviceInfo | null) {
    if (!device) {
      return
    }
    this.adapter.selectMediaDevice(device)
  }

  async getPreviewMediaStream(device: MediaDeviceInfo) {
    switch (device.kind) {
      case 'videoinput':
        this.setPreviewVideoStream(
          await navigator.mediaDevices.getUserMedia({
            video: { deviceId: device.deviceId },
          })
        )
        break
      case 'audioinput':
        this.setPreviewAudioStream(
          await navigator.mediaDevices.getUserMedia({
            audio: { deviceId: device.deviceId },
          })
        )
        break
    }
  }

  @action.bound
  private setPreviewAudioStream(mediaStream: MediaStream) {
    this.previewAudioStream = mediaStream
  }

  @action.bound
  private setPreviewVideoStream(mediaStream: MediaStream) {
    this.previewVideoStream = mediaStream
  }

  @action.bound
  show() {
    this.showing = true
  }

  @action.bound
  hide() {
    this.showing = false
  }
}
